(() => {
  const attributionQueryParam = 'ccid';
  const attributionCartAttribute = '__diggnit_concierge_id';
  const attributionLineItemProperty = '_diggnit_concierge_id';
  const attributionLineItemInputName = `properties[${attributionLineItemProperty}]`;
  const attributionStorageKey = 'diggnit_concierge_attribution_v1';
  const attributionStorageTtlMs = 7 * 24 * 60 * 60 * 1000;
  const attributionIdPattern = /^cc_[A-Za-z0-9_-]{20,64}$/;
  const attributionRevocationEndpoint =
    'https://diggnitcustomersupport.floot.app/_api/concierge-attribution-revoke';
  const productAddFormSelector = 'form[data-type="add-to-cart-form"]';
  const attributionLineItemInputSelector = `input[name="${attributionLineItemInputName}"]`;
  let pendingAttributionId = null;
  let pendingAttributionExpiresAt = null;
  let lastWrittenAttributionId = null;
  let activeAttributionExpiresAt = null;
  let attributionWriteInFlight = false;
  let attributionWrittenCallback = null;
  let attributionRevokedCallback = null;
  let customerPrivacyLoadRequested = false;
  let productFormObserver = null;
  let attributionExpiryTimer = null;

  const marketingAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
    } catch {
      return false;
    }
  };

  const marketingPermissionKnown = () => (
    typeof window.Shopify?.customerPrivacy?.marketingAllowed === 'function'
  );

  const isValidAttributionId = (value) => (
    typeof value === 'string' && attributionIdPattern.test(value)
  );

  const normalizeAttributionExpiry = (value) => {
    if (value === undefined || value === null || value === '') return null;

    let expiry = null;
    if (typeof value === 'number') {
      expiry = value;
    } else if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) return null;
      expiry = /^\d+$/.test(trimmedValue)
        ? Number(trimmedValue)
        : Date.parse(trimmedValue);
    }

    if (!Number.isFinite(expiry)) return null;
    if (expiry > 0 && expiry < 1_000_000_000_000) expiry *= 1000;

    const now = Date.now();
    return expiry > now && expiry <= now + attributionStorageTtlMs
      ? expiry
      : null;
  };

  const cartUpdateUrl = () => {
    const configuredUrl = window.routes?.cart_update_url;
    if (typeof configuredUrl === 'string' && configuredUrl) {
      return configuredUrl.endsWith('.js') ? configuredUrl : `${configuredUrl}.js`;
    }

    const root = window.Shopify?.routes?.root || '/';
    return `${root.endsWith('/') ? root : `${root}/`}cart/update.js`;
  };

  const clearAttributionShadow = () => {
    try {
      window.localStorage?.removeItem(attributionStorageKey);
    } catch {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  };

  const loadAttributionShadow = () => {
    if (!marketingAllowed()) return null;

    try {
      const rawValue = window.localStorage?.getItem(attributionStorageKey);
      if (!rawValue) return null;

      const parsedValue = JSON.parse(rawValue);
      const now = Date.now();
      const hasValidLifetime = Number.isFinite(parsedValue?.expiresAt)
        && parsedValue.expiresAt > now
        && parsedValue.expiresAt <= now + attributionStorageTtlMs;

      if (!isValidAttributionId(parsedValue?.id) || !hasValidLifetime) {
        clearAttributionShadow();
        return null;
      }

      return {
        id: parsedValue.id,
        expiresAt: parsedValue.expiresAt,
      };
    } catch {
      clearAttributionShadow();
      return null;
    }
  };

  const persistAttributionShadow = (attributionId, expiresAt) => {
    if (!marketingAllowed() || !isValidAttributionId(attributionId)) return false;

    const now = Date.now();
    if (
      !Number.isFinite(expiresAt)
      || expiresAt <= now
      || expiresAt > now + attributionStorageTtlMs
    ) {
      return false;
    }

    try {
      window.localStorage?.setItem(
        attributionStorageKey,
        JSON.stringify({
          id: attributionId,
          expiresAt,
        })
      );
      return true;
    } catch {
      return false;
    }
  };

  const getProductAddForms = (root = document) => {
    if (typeof root?.querySelectorAll !== 'function') return [];
    return Array.from(root.querySelectorAll(productAddFormSelector));
  };

  const syncProductAddForm = (form, attributionId = null) => {
    if (
      !form
      || typeof form.querySelectorAll !== 'function'
      || typeof document.createElement !== 'function'
    ) {
      return;
    }

    const existingInputs = Array.from(form.querySelectorAll(attributionLineItemInputSelector));
    const shouldAttach = marketingAllowed()
      && isValidAttributionId(attributionId)
      && attributionId === lastWrittenAttributionId
      && Number.isFinite(activeAttributionExpiresAt)
      && activeAttributionExpiresAt > Date.now();

    if (!shouldAttach) {
      existingInputs.forEach((input) => input.remove());
      return;
    }

    const input = existingInputs.shift() || document.createElement('input');
    input.type = 'hidden';
    input.name = attributionLineItemInputName;
    input.value = attributionId;
    if (!input.parentNode && typeof form.appendChild === 'function') form.appendChild(input);
    existingInputs.forEach((duplicateInput) => duplicateInput.remove());
  };

  const syncProductFormAttribution = (attributionId = null) => {
    getProductAddForms().forEach((form) => syncProductAddForm(form, attributionId));
  };

  const stopProductFormObserver = () => {
    productFormObserver?.disconnect();
    productFormObserver = null;
  };

  const observeProductAddForms = () => {
    if (
      productFormObserver
      || typeof MutationObserver !== 'function'
      || !document.documentElement
    ) {
      return;
    }

    productFormObserver = new MutationObserver((mutations) => {
      if (
        !marketingAllowed()
        || !isValidAttributionId(lastWrittenAttributionId)
        || !Number.isFinite(activeAttributionExpiresAt)
        || activeAttributionExpiresAt <= Date.now()
      ) {
        syncProductFormAttribution();
        stopProductFormObserver();
        return;
      }

      mutations.forEach((mutation) => {
        Array.from(mutation.addedNodes || []).forEach((node) => {
          if (node?.nodeType !== 1) return;
          if (node.matches?.(productAddFormSelector)) {
            syncProductAddForm(node, lastWrittenAttributionId);
          }
          getProductAddForms(node).forEach((form) => {
            syncProductAddForm(form, lastWrittenAttributionId);
          });
        });
      });
    });
    productFormObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  const clearAttributionExpiryTimer = () => {
    if (attributionExpiryTimer === null || typeof window.clearTimeout !== 'function') return;
    window.clearTimeout(attributionExpiryTimer);
    attributionExpiryTimer = null;
  };

  const clearConsentGatedAttribution = (notifyRevocation = false) => {
    const attributionIdToRevoke =
      lastWrittenAttributionId || pendingAttributionId;
    pendingAttributionId = null;
    pendingAttributionExpiresAt = null;
    lastWrittenAttributionId = null;
    activeAttributionExpiresAt = null;
    clearAttributionExpiryTimer();
    clearAttributionShadow();
    syncProductFormAttribution();
    stopProductFormObserver();
    if (notifyRevocation && attributionIdToRevoke) {
      attributionRevokedCallback?.(attributionIdToRevoke);
      void Promise.allSettled([
        window.fetch(attributionRevocationEndpoint, {
          method: 'POST',
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          keepalive: true,
          // A string body without custom headers is CORS-safelisted and avoids
          // a preflight that Floot's edge currently answers without CORS headers.
          body: JSON.stringify({ attributionId: attributionIdToRevoke }),
        }),
        window.fetch(cartUpdateUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            attributes: {
              [attributionCartAttribute]: '',
            },
          }),
        }),
      ]);
    }
  };

  const scheduleAttributionExpiry = (expiresAt) => {
    clearAttributionExpiryTimer();
    if (!Number.isFinite(expiresAt) || typeof window.setTimeout !== 'function') return;

    const delay = Math.max(0, expiresAt - Date.now());
    attributionExpiryTimer = window.setTimeout(() => {
      if (
        Number.isFinite(activeAttributionExpiresAt)
        && activeAttributionExpiresAt <= Date.now()
      ) {
        clearConsentGatedAttribution();
      }
    }, delay);
  };

  const activateProductFormAttribution = (attributionId, expiresAt) => {
    activeAttributionExpiresAt = expiresAt;
    syncProductFormAttribution(attributionId);
    observeProductAddForms();
    scheduleAttributionExpiry(expiresAt);
  };

  const writePendingAttribution = async () => {
    if (
      !pendingAttributionId
      || attributionWriteInFlight
      || !marketingAllowed()
    ) {
      return false;
    }

    if (pendingAttributionId === lastWrittenAttributionId) {
      pendingAttributionId = null;
      pendingAttributionExpiresAt = null;
      return false;
    }

    const attributionId = pendingAttributionId;
    const inheritedExpiresAt = pendingAttributionExpiresAt;
    attributionWriteInFlight = true;

    try {
      const response = await window.fetch(cartUpdateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          attributes: {
            [attributionCartAttribute]: attributionId,
          },
        }),
      });

      if (!response.ok) throw new Error('Unable to persist concierge attribution');

      if (
        !marketingAllowed()
        || pendingAttributionId !== attributionId
      ) {
        return false;
      }

      const now = Date.now();
      const expiresAt = Number.isFinite(inheritedExpiresAt)
        ? inheritedExpiresAt
        : now + attributionStorageTtlMs;
      if (expiresAt <= now || expiresAt > now + attributionStorageTtlMs) {
        pendingAttributionId = null;
        pendingAttributionExpiresAt = null;
        clearAttributionShadow();
        return false;
      }

      lastWrittenAttributionId = attributionId;
      pendingAttributionId = null;
      pendingAttributionExpiresAt = null;
      persistAttributionShadow(attributionId, expiresAt);
      activateProductFormAttribution(attributionId, expiresAt);
      attributionWrittenCallback?.(attributionId);
      return true;
    } catch {
      // Keep the in-memory value for an online or consent-change retry.
      return false;
    } finally {
      attributionWriteInFlight = false;
      if (
        pendingAttributionId
        && pendingAttributionId !== attributionId
        && marketingAllowed()
      ) {
        void writePendingAttribution();
      }
    }
  };

  const retryKnownAttribution = () => {
    if (!marketingAllowed()) return false;

    if (!pendingAttributionId) {
      const storedAttribution = loadAttributionShadow();
      if (storedAttribution) {
        pendingAttributionId = storedAttribution.id;
        pendingAttributionExpiresAt = storedAttribution.expiresAt;
      }
    }

    if (!pendingAttributionId) return false;
    void writePendingAttribution();
    return true;
  };

  const ensureCustomerPrivacyApi = () => {
    if (
      customerPrivacyLoadRequested
      || window.Shopify?.customerPrivacy
      || typeof window.Shopify?.loadFeatures !== 'function'
    ) {
      return;
    }

    customerPrivacyLoadRequested = true;
    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1',
        },
      ],
      (error) => {
        if (error) return;
        if (marketingAllowed()) {
          retryKnownAttribution();
        } else {
          clearAttributionShadow();
        }
      }
    );
  };

  const acceptAttributionId = (value, suppliedExpiresAt) => {
    if (!isValidAttributionId(value)) return false;

    const hasSuppliedExpiry = (
      suppliedExpiresAt !== undefined
      && suppliedExpiresAt !== null
      && suppliedExpiresAt !== ''
    );
    const normalizedExpiry = hasSuppliedExpiry
      ? normalizeAttributionExpiry(suppliedExpiresAt)
      : null;
    if (hasSuppliedExpiry && normalizedExpiry === null) return false;

    pendingAttributionId = value;
    pendingAttributionExpiresAt = normalizedExpiry;
    ensureCustomerPrivacyApi();
    void writePendingAttribution();
    return true;
  };

  const consumeAttributionFromUrl = () => {
    let pageUrl;

    try {
      pageUrl = new URL(window.location.href);
    } catch {
      return null;
    }

    if (!pageUrl.searchParams.has(attributionQueryParam)) return null;
    const attributionId = pageUrl.searchParams.get(attributionQueryParam);
    pageUrl.searchParams.delete(attributionQueryParam);

    try {
      window.history.replaceState(window.history.state, '', pageUrl.toString());
    } catch {
      // Navigation remains usable even when a browser blocks history updates.
    }

    return typeof attributionId === 'string' && attributionIdPattern.test(attributionId)
      ? attributionId
      : null;
  };

  document.addEventListener('visitorConsentCollected', () => {
    if (!marketingAllowed()) {
      clearConsentGatedAttribution(true);
      return;
    }
    retryKnownAttribution();
  });
  window.addEventListener('online', () => {
    retryKnownAttribution();
  });
  window.addEventListener('load', () => {
    ensureCustomerPrivacyApi();
    retryKnownAttribution();
  });

  const initialAttributionId = consumeAttributionFromUrl();
  if (marketingPermissionKnown() && !marketingAllowed()) clearAttributionShadow();
  if (initialAttributionId) {
    acceptAttributionId(initialAttributionId);
  } else {
    ensureCustomerPrivacyApi();
    retryKnownAttribution();
  }

  const launcher = document.getElementById('diggn-chat-button');
  const dialog = document.getElementById('diggn-chat-container');
  const closeButton = dialog?.querySelector('[data-diggn-concierge-close]');
  const frameHost = dialog?.querySelector('[data-diggn-concierge-frame]');

  if (!launcher || !dialog || !closeButton || !frameHost) return;

  const supportOrigin = dialog.dataset.supportOrigin;
  const supportPath = dialog.dataset.supportPath || '/chatbot-embed';
  const initialLocale = new URL(supportPath, supportOrigin).searchParams.get('locale') === 'ar' ? 'ar' : 'en';
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const mobileMode = launcher.dataset.mobileMode || 'inline';
  const allowedPageTypes = new Set([
    '404',
    'article',
    'blog',
    'cart',
    'collection',
    'index',
    'page',
    'password',
    'policy',
    'product',
    'search',
  ]);
  const pageType = allowedPageTypes.has(launcher.dataset.pageType)
    ? launcher.dataset.pageType
    : 'other';
  const invitationCopy = initialLocale === 'ar'
    ? {
        message: 'محتار وش يناسبك؟ أساعدك تختار.',
        mobileMessage: 'محتار وش يناسبك؟ اسألني.',
        openLabel: 'افتح مستشار العناية لمساعدتك في الاختيار',
        dismissLabel: 'إخفاء رسالة مستشار العناية',
      }
    : {
        message: 'Not sure what fits? I can help you choose.',
        mobileMessage: 'Need help choosing? Ask me.',
        openLabel: 'Open the grooming concierge for help choosing',
        dismissLabel: 'Dismiss the concierge invitation',
      };
  const invitation = document.createElement('div');
  const invitationMessage = document.createElement('button');
  const invitationClose = document.createElement('button');

  invitation.className = 'diggn-concierge-invitation';
  invitation.dataset.mobileMode = launcher.dataset.mobileMode || 'inline';
  invitation.hidden = true;
  invitationMessage.className = 'diggn-concierge-invitation__message';
  invitationMessage.type = 'button';
  invitationMessage.setAttribute('aria-label', invitationCopy.openLabel);
  invitationClose.className = 'diggn-concierge-invitation__close';
  invitationClose.type = 'button';
  invitationClose.textContent = '×';
  invitationClose.setAttribute('aria-label', invitationCopy.dismissLabel);
  invitation.append(invitationMessage, invitationClose);
  launcher.insertAdjacentElement('beforebegin', invitation);

  dialog.dataset.conciergeLocale = initialLocale;
  let iframe = null;
  let previouslyFocused = null;
  let invitationShown = false;
  let invitationAutoHideTimer = null;
  let invitationHideTimer = null;
  let mobileObstructionFrame = null;
  const launcherInitialTabIndex = launcher.getAttribute('tabindex');

  const invitationSessionKey = 'diggn_concierge_invitation_seen_v1';
  const invitationDelayMs = 10000;
  const invitationAutoHideMs = 10000;
  const invitationScrollThreshold = 0.4;
  const invitationMinimumDelayMs = 5000;
  const invitationStartedAt = Date.now();

  const analyticsAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch {
      return false;
    }
  };

  const measure = (eventName, detail = {}) => {
    const payload = {
      event: eventName,
      page_type: pageType,
      ...detail,
    };

    document.dispatchEvent(new CustomEvent('diggn:track', { detail: payload }));
    if (analyticsAllowed()) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    }
  };

  attributionWrittenCallback = (attributionId) => {
    measure('concierge_attribution_carried');
    iframe?.contentWindow?.postMessage(
      {
        type: 'concierge:attribution-carried',
        attributionId,
      },
      supportOrigin
    );
  };

  attributionRevokedCallback = (attributionId) => {
    iframe?.contentWindow?.postMessage(
      {
        type: 'concierge:attribution-revoked',
        attributionId,
      },
      supportOrigin
    );
  };

  const invitationWasSeen = () => {
    try {
      return window.sessionStorage.getItem(invitationSessionKey) === '1';
    } catch {
      return invitationShown;
    }
  };

  const markInvitationSeen = () => {
    try {
      window.sessionStorage.setItem(invitationSessionKey, '1');
    } catch {
      // Session storage can be unavailable in privacy-restricted browsers.
    }
  };

  const isMobileTeaser = () => mobileQuery.matches && mobileMode === 'teaser';

  const updateInvitationMessage = () => {
    invitationMessage.textContent = isMobileTeaser()
      ? invitationCopy.mobileMessage
      : invitationCopy.message;
  };

  const isVisibleBottomControl = (element) => {
    if (!element || !isMobileTeaser()) return false;
    if (
      element === launcher
      || element === invitation
      || element === dialog
      || element.closest?.(
        '.diggn-concierge-launcher, .diggn-concierge-invitation, .diggn-concierge-dialog'
      )
    ) {
      return false;
    }

    const styles = window.getComputedStyle(element);
    if (
      styles.display === 'none'
      || styles.visibility === 'hidden'
      || styles.opacity === '0'
      || styles.pointerEvents === 'none'
      || (styles.position !== 'fixed' && styles.position !== 'sticky')
    ) {
      return false;
    }

    const bounds = element.getBoundingClientRect();
    if (bounds.width < 36 || bounds.height < 30) return false;
    if (bounds.bottom <= 0 || bounds.top >= window.innerHeight || bounds.top < window.innerHeight * 0.35) {
      return false;
    }

    const isWide = bounds.width >= window.innerWidth * 0.45;
    const isNearPhysicalRight = bounds.right >= window.innerWidth - 96;

    return isWide || isNearPhysicalRight;
  };

  const isVisibleFooterControl = (element) => {
    if (!element || !isMobileTeaser()) return false;
    const styles = window.getComputedStyle(element);
    if (
      styles.display === 'none'
      || styles.visibility === 'hidden'
      || styles.opacity === '0'
      || styles.pointerEvents === 'none'
    ) {
      return false;
    }

    const bounds = element.getBoundingClientRect();
    if (bounds.width < 20 || bounds.height < 20) return false;
    if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) return false;
    return bounds.right >= window.innerWidth - 96;
  };

  const hasModalObstruction = () => {
    if (!isMobileTeaser() || !dialog.hidden) return false;
    return Boolean(
      document.querySelector(
        'cart-drawer.active, quick-add-modal[open], .quick-add-modal[open], [data-quick-add-modal].active'
      )
    );
  };

  const getMobileBottomOffset = () => {
    const mobileGap = 12;
    let offset = mobileGap;
    const knownControls = Array.from(
      document.querySelectorAll(
        '#shopify-pc__banner, [data-shopify-privacy-banner], [data-global-mobile-utility].is-visible, [data-pdp-sticky-bar].is-visible, [data-pdp-sticky-bar].is-utility-visible, .beard-density-launch__mobile-cta, #PBarNextFrameWrapper, #appstle_loyalty_points_float, #appstle_widget, [id*="loyal"], [id*="reward"], [id*="whatsapp"]'
      )
    );
    const controls = new Set([...knownControls, ...document.body.children]);

    const visibleControls = Array.from(controls)
      .filter(isVisibleBottomControl)
      .map((element) => element.getBoundingClientRect());

    for (let pass = 0; pass < 4; pass += 1) {
      let nextOffset = offset;
      visibleControls.forEach((bounds) => {
        if (bounds.bottom < window.innerHeight - offset - 24) return;
        nextOffset = Math.max(nextOffset, window.innerHeight - bounds.top + mobileGap);
      });
      if (nextOffset === offset) break;
      offset = nextOffset;
    }

    const footer = document.querySelector('footer');
    const footerBounds = footer?.getBoundingClientRect();
    if (footerBounds && footerBounds.top < window.innerHeight && footerBounds.bottom > 0) {
      const footerControls = footer.querySelectorAll(
        'a, button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])'
      );
      footerControls.forEach((element) => {
        if (!isVisibleFooterControl(element)) return;
        const bounds = element.getBoundingClientRect();
        offset = Math.max(offset, window.innerHeight - bounds.top + mobileGap);
      });
    }

    const launcherSize = launcher.getBoundingClientRect().height || 48;
    return Math.min(Math.ceil(offset), Math.max(mobileGap, window.innerHeight - launcherSize - mobileGap));
  };

  const setModalObstructed = (isObstructed) => {
    launcher.classList.toggle('is-modal-obstructed', isObstructed);
    invitation.classList.toggle('is-modal-obstructed', isObstructed);

    if (isObstructed) {
      if (launcher.getAttribute('aria-hidden') !== 'true') launcher.setAttribute('aria-hidden', 'true');
      if (launcher.getAttribute('tabindex') !== '-1') launcher.setAttribute('tabindex', '-1');
      return;
    }

    if (launcher.hasAttribute('aria-hidden')) launcher.removeAttribute('aria-hidden');
    if (launcherInitialTabIndex === null) {
      if (launcher.hasAttribute('tabindex')) launcher.removeAttribute('tabindex');
    } else if (launcher.getAttribute('tabindex') !== launcherInitialTabIndex) {
      launcher.setAttribute('tabindex', launcherInitialTabIndex);
    }
  };

  const syncMobileObstruction = () => {
    mobileObstructionFrame = null;
    if (!isMobileTeaser()) {
      document.documentElement.style.removeProperty('--diggn-concierge-mobile-bottom-offset');
      setModalObstructed(false);
      return;
    }

    const isObstructed = hasModalObstruction();
    setModalObstructed(isObstructed);
    if (isObstructed) {
      if (invitation.classList.contains('is-visible')) hideInvitation('modal_obstruction');
      return;
    }

    document.documentElement.style.setProperty(
      '--diggn-concierge-mobile-bottom-offset',
      `${getMobileBottomOffset()}px`
    );
  };

  const scheduleMobileObstructionSync = () => {
    if (mobileObstructionFrame) return;
    mobileObstructionFrame = window.requestAnimationFrame(syncMobileObstruction);
  };

  const launcherIsVisible = () => {
    const styles = window.getComputedStyle(launcher);
    const bounds = launcher.getBoundingClientRect();
    return (
      styles.display !== 'none'
      && styles.visibility !== 'hidden'
      && bounds.width > 0
      && bounds.height > 0
    );
  };

  const clearInvitationAutoHide = () => {
    if (!invitationAutoHideTimer) return;
    window.clearTimeout(invitationAutoHideTimer);
    invitationAutoHideTimer = null;
  };

  const finishInvitationHide = () => {
    if (invitationHideTimer) {
      window.clearTimeout(invitationHideTimer);
      invitationHideTimer = null;
    }
    invitation.hidden = true;
  };

  const hideInvitation = (reason, restoreLauncherFocus = false, recordDismissal = false) => {
    if (invitation.hidden) return;

    clearInvitationAutoHide();
    invitation.classList.remove('is-visible');
    invitationHideTimer = window.setTimeout(finishInvitationHide, 180);

    if (recordDismissal) {
      measure('concierge_invitation_dismiss', { reason });
    }

    if (restoreLauncherFocus) {
      window.requestAnimationFrame(() => launcher.focus({ preventScroll: true }));
    }
  };

  const showInvitation = (trigger) => {
    syncMobileObstruction();
    if (
      invitationShown
      || invitationWasSeen()
      || !dialog.hidden
      || document.visibilityState !== 'visible'
      || !launcherIsVisible()
    ) {
      return false;
    }

    invitationShown = true;
    markInvitationSeen();
    invitation.hidden = false;
    window.requestAnimationFrame(() => invitation.classList.add('is-visible'));
    measure('concierge_invitation_view', { trigger });
    invitationAutoHideTimer = window.setTimeout(
      () => hideInvitation('timeout', false, true),
      invitationAutoHideMs
    );
    return true;
  };

  const requestInvitation = (trigger) => {
    const remainingDelay = invitationMinimumDelayMs - (Date.now() - invitationStartedAt);
    if (remainingDelay > 0) {
      window.setTimeout(() => showInvitation(trigger), remainingDelay);
      return;
    }
    showInvitation(trigger);
  };

  const ensureIframe = () => {
    if (iframe) return iframe;

    const iframeUrl = new URL(supportPath, supportOrigin);
    iframeUrl.searchParams.set('host', 'shopify');
    iframe = document.createElement('iframe');
    iframe.src = iframeUrl.toString();
    iframe.title = dialog.dataset.iframeTitle || "Diggn'It Grooming Concierge";
    iframe.loading = 'lazy';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allow = 'autoplay; clipboard-write';
    frameHost.appendChild(iframe);
    return iframe;
  };

  const closeDialog = () => {
    if (dialog.hidden) return;
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: 'concierge:host-close' },
        supportOrigin
      );
    }
    dialog.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('diggn-concierge-is-open');
    measure('concierge_close');
    (previouslyFocused || launcher).focus({ preventScroll: true });
  };

  const openDialog = (source = 'floating_launcher') => {
    if (!dialog.hidden) return;
    previouslyFocused = source === 'proactive_invite'
      || source === 'mobile_teaser'
      || source === 'mobile_portrait'
      || source === 'floating_launcher'
      ? launcher
      : document.activeElement instanceof HTMLElement
        ? document.activeElement
        : launcher;
    markInvitationSeen();
    hideInvitation('opened');
    ensureIframe();
    dialog.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    document.body.classList.add('diggn-concierge-is-open');
    measure('concierge_open', { source });
    closeButton.focus({ preventScroll: true });
  };

  launcher.addEventListener('click', () => {
    if (dialog.hidden) openDialog(isMobileTeaser() ? 'mobile_portrait' : 'floating_launcher');
    else closeDialog();
  });

  invitationMessage.addEventListener('click', () => {
    openDialog(isMobileTeaser() ? 'mobile_teaser' : 'proactive_invite');
  });

  invitationClose.addEventListener('click', () => {
    hideInvitation('manual', true, true);
  });

  invitation.addEventListener('pointerenter', clearInvitationAutoHide);
  invitation.addEventListener('focusin', clearInvitationAutoHide);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-diggn-open-support]');
    if (!trigger) return;
    event.preventDefault();

    const headerDrawer = trigger.closest('header-drawer');
    const drawerSummary = headerDrawer?.querySelector('details > summary');
    if (headerDrawer?.closeMenuDrawer && drawerSummary) {
      headerDrawer.closeMenuDrawer(event, drawerSummary);
    }

    openDialog('inline_recommendation');
  });

  closeButton.addEventListener('click', closeDialog);

  document.addEventListener('diggn:open-support', () => openDialog('custom_event'));

  document.addEventListener('keydown', (event) => {
    if (dialog.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      dialog.querySelectorAll('button:not([disabled]), iframe, [href], [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.hasAttribute('hidden'));

    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('message', (event) => {
    if (event.origin !== supportOrigin) return;
    if (!iframe || event.source !== iframe.contentWindow) return;
    if (!event.data || typeof event.data !== 'object') return;
    if (event.data.type === 'chatbot:close') closeDialog();
    if (event.data.type === 'concierge:attribution') {
      acceptAttributionId(event.data.attributionId, event.data.expiresAt);
    }
    if (
      event.data.type === 'concierge:locale'
      && (event.data.locale === 'en' || event.data.locale === 'ar')
    ) {
      dialog.dataset.conciergeLocale = event.data.locale;
    }
  });

  if (!invitationWasSeen()) {
    window.setTimeout(() => showInvitation('delay'), invitationDelayMs);

    const handleInvitationScroll = () => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;
      const scrollProgress = window.scrollY / scrollableHeight;
      if (scrollProgress < invitationScrollThreshold) return;

      window.removeEventListener('scroll', handleInvitationScroll);
      requestInvitation('scroll');
    };

    window.addEventListener('scroll', handleInvitationScroll, { passive: true });
  }

  window.addEventListener('resize', () => {
    updateInvitationMessage();
    scheduleMobileObstructionSync();
    if (!invitation.hidden && !launcherIsVisible()) {
      hideInvitation('launcher_hidden');
    }
  }, { passive: true });

  window.addEventListener('orientationchange', scheduleMobileObstructionSync, { passive: true });
  window.addEventListener('scroll', scheduleMobileObstructionSync, { passive: true });
  document.addEventListener('diggn:mobile-layout-change', scheduleMobileObstructionSync);
  new MutationObserver(scheduleMobileObstructionSync).observe(document.body, {
    attributes: true,
    attributeFilter: ['class', 'hidden', 'open'],
    childList: true,
    subtree: true,
  });
  window.setTimeout(scheduleMobileObstructionSync, 1000);
  window.setTimeout(scheduleMobileObstructionSync, 3000);
  updateInvitationMessage();
  syncMobileObstruction();
})();
